import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { urlFiltersToCioFacets } from '~/grid/utils/cioUtils';
import useProfileSizes from '~/pdp/utils/useProfileSizes';
import useGender from '~/shared/utils/useGender';

import { getAutoFilterParams } from '../utils/autofilters';
import { getNewQueryParamString, QueryParamBehavior } from '../utils/routing';

/**
 * This hook is used to apply automatic filters to the product grid based on the user's
 * size preferences and the grid gender. It only applies the filters if the user
 * has not already selected them in the URL and if the filters are valid (i.e., they exist
 * in the cioFacets for the current grid). E.g. do not include "size_top" for a bottoms only grid
 */
export const useAutoFilters = ({
  builderState,
  gridGender,
  cioFacets = [],
}) => {
  const router = useRouter();
  const [autofilterApplied, setAutofilterApplied] = useState(false);
  const { profileSizes } = useProfileSizes({ isPdp: false });
  const { gender: userGender } = useGender();

  const automaticSizeFiltering =
    builderState?.content?.data?.automaticSizeFiltering;

  const autofilterParams = getAutoFilterParams({
    gridGender,
    profileSizes,
    userGender,
    automaticSizeFilteringType: automaticSizeFiltering,
  });

  const validAutofilterParams = Object.entries(autofilterParams).reduce(
    (acc, [key, value]) => {
      // Exclude filters that do not exist in cioFacets for the current grid,
      // e.g. do not include "size_top" for a bottoms only grid
      const mappedKey = urlFiltersToCioFacets[key] || key;
      const cioFacet = cioFacets?.find((facet) => facet?.name === mappedKey);
      if (!cioFacet) {
        return acc;
      }

      // Exclude empty filters
      if (Array.isArray(value) && value.length > 0) {
        return { ...acc, [key]: value };
      }

      // Exclude empty strings
      if (typeof value === 'string' && value !== '') {
        return { ...acc, [key]: value };
      }

      return acc;
    },
    {}
  );

  useEffect(() => {
    // If there are no valid autofilter params (either already selected in url or empty)
    // or no cioFacets (due to fetching not complete), do nothing. This useEffect will re-run
    // when the cioFacets are fetched and the validAutofilterParams are updated.
    if (!validAutofilterParams || !cioFacets?.length) {
      return;
    }

    if (!autofilterApplied) {
      const [asPathname, query] = router.asPath.split('?');
      const newQueryParamString = getNewQueryParamString({
        query,
        newQueryParams: validAutofilterParams,
        queryParamBehavior: QueryParamBehavior.APPEND,
      });

      if (!newQueryParamString) {
        setAutofilterApplied(true);
        return;
      }

      const handleRouteChangeComplete = () => {
        setAutofilterApplied(true);
        router.events.off('routeChangeComplete', handleRouteChangeComplete);
      };

      router.events.on('routeChangeComplete', handleRouteChangeComplete);

      router.replace(router.pathname, `${asPathname}?${newQueryParamString}`, {
        shallow: true,
      });
    }
  }, [autofilterApplied, cioFacets.length, validAutofilterParams, router]);

  return {
    allAutofilterParams: autofilterParams,
    validAutofilterParams,
    autofilterApplied,
  };
};
